import { Notify } from 'quasar'
import { i18n } from '@/boot/i18n'
import { usePostSaleReceiptStore } from '@/stores/postSaleReceipt'
import { printSaleViaVolcoraThermal, resolveBusinessInfoForPrint } from '@/composables/useVolcoraReceiptPrinter'
import { isUsPhoneNumber } from '@/utils/phone'
import { posService } from './POSService'
import { customerService } from './CustomerService'
import { smsService } from './SmsService'
import { shareReceiptPdfOnIos, shouldUseNativeReceiptPdfShare } from './receiptShareIos'
import {
  customerDisplayAckReceiptChoice,
  customerDisplayDismissPaymentComplete,
  customerDisplayService
} from './CustomerDisplay'

const t = (key, ...args) => i18n.global.t(key, ...args)

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

/** Do not block cashier receipt UI if Android CFD dismiss hangs after activity resume. */
const CFD_ACK_TIMEOUT_MS = 4000

let receiptChoiceChain = Promise.resolve()

async function ackReceiptChoiceSafely (payload) {
  try {
    await Promise.race([
      customerDisplayAckReceiptChoice(payload),
      new Promise((resolve) => setTimeout(resolve, CFD_ACK_TIMEOUT_MS)),
    ])
  } catch {
    /* ignore */
  }
  void customerDisplayDismissPaymentComplete()
}

function withReceiptChoiceMutex (fn) {
  const run = receiptChoiceChain.then(fn, fn)
  receiptChoiceChain = run.catch(() => {})
  return run
}

function resolveCustomerId (sale) {
  return sale?.customer_id ?? sale?.customer?.id ?? null
}

async function syncCustomerEmail (sale, email) {
  const customerId = resolveCustomerId(sale)
  if (!customerId || !email) return
  const customer = sale.customer || {}
  const primary = customer.primary_email
  if (primary?.id) {
    if (String(primary.email || '').toLowerCase() === email.toLowerCase()) return
    await customerService.updateCustomerEmail(customerId, primary.id, { email, is_primary: true })
    return
  }
  const fromList = Array.isArray(customer.emails)
    ? (customer.emails.find(e => e.is_primary) || customer.emails[0])
    : null
  if (fromList?.id) {
    await customerService.updateCustomerEmail(customerId, fromList.id, { email, is_primary: true })
  } else {
    await customerService.addCustomerEmail(customerId, { email, is_primary: true })
  }
}

async function syncCustomerPhone (sale, phone) {
  const customerId = resolveCustomerId(sale)
  if (!customerId || !phone) return
  const customer = sale.customer || {}
  const primary = customer.primary_phone
  if (primary?.id) {
    if (String(primary.phone || '') === phone) return
    await customerService.updateCustomerPhone(customerId, primary.id, { phone, is_primary: true })
    return
  }
  const fromList = Array.isArray(customer.phones)
    ? (customer.phones.find(p => p.is_primary) || customer.phones[0])
    : null
  if (fromList?.id) {
    await customerService.updateCustomerPhone(customerId, fromList.id, { phone, is_primary: true })
  } else {
    await customerService.addCustomerPhone(customerId, { phone, is_primary: true })
  }
}

async function resolveBusinessInfoForPrintFromStore (logoUrlFallback) {
  return resolveBusinessInfoForPrint(logoUrlFallback)
}

async function runPrint (sale, { logoUrlFallback } = {}) {
  const saleId = sale?.id
  if (!saleId) {
    throw new Error(t('sales.postSaleReceiptMissingSale'))
  }

  if (shouldUseNativeReceiptPdfShare()) {
    await shareReceiptPdfOnIos(saleId)
    return { ok: true, used: true }
  }

  const businessInfo = await resolveBusinessInfoForPrintFromStore(logoUrlFallback)
  const thermal = await printSaleViaVolcoraThermal({
    sale,
    businessInfo,
    t,
    logoUrlFallback
  })

  if (thermal.used && thermal.ok) {
    return { ok: true, used: true, thermal: true }
  }

  if (thermal.used && !thermal.ok) {
    const cap = thermal.errorDetail || (thermal.error ? String(thermal.error) : undefined)
    Notify.create({
      type: 'warning',
      message: t('sales.receiptThermalFallback'),
      caption: cap,
      multiLine: !!thermal.errorDetail
    })
  }

  return { ok: true, used: false, needsBrowserPrint: true, sale }
}

async function runEmail (sale, { email, updateCustomer, ccMe }) {
  const trimmed = String(email || '').trim()
  if (!trimmed || !EMAIL_RE.test(trimmed)) {
    throw new Error(t('sales.postSaleReceiptInvalidEmail'))
  }
  if (updateCustomer) {
    try {
      await syncCustomerEmail(sale, trimmed)
    } catch (e) {
      console.warn('[postSaleReceipt] customer email sync failed', e)
    }
  }
  await posService.emailReceipt(sale.id, {
    to: trimmed,
    cc_user: !!ccMe
  })
}

async function runSms (sale, { phone, updateCustomer }) {
  const trimmed = String(phone || '').trim()
  if (!trimmed || !isUsPhoneNumber(trimmed)) {
    throw new Error(t('sales.postSaleReceiptInvalidPhone'))
  }
  const { opted_out } = await smsService.getOptOutStatus(trimmed)
  if (opted_out) {
    throw new Error(t('common.smsOptedOut'))
  }
  if (updateCustomer) {
    try {
      await syncCustomerPhone(sale, trimmed)
    } catch (e) {
      console.warn('[postSaleReceipt] customer phone sync failed', e)
    }
  }
  await posService.smsReceipt(sale.id, { to: trimmed })
}

/**
 * @param {object} params
 * @param {number|string} [params.saleId]
 * @param {'print'|'email'|'sms'|'none'} params.choice
 * @param {string} [params.email]
 * @param {string} [params.phone]
 * @param {boolean} [params.updateCustomer]
 * @param {boolean} [params.ccMe]
 * @param {'cashier'|'cfd'} [params.source]
 * @param {string} [params.logoUrlFallback]
 */
export function executePostSaleReceiptChoice (params) {
  return withReceiptChoiceMutex(() => executePostSaleReceiptChoiceImpl(params))
}

async function executePostSaleReceiptChoiceImpl ({
  saleId: saleIdParam,
  choice,
  email,
  phone,
  updateCustomer = false,
  ccMe = false,
  source = 'cashier',
  logoUrlFallback
}) {
  const store = usePostSaleReceiptStore()
  const saleId = saleIdParam ?? store.pendingSaleId
  const targetSaleId = saleId != null ? String(saleId) : null

  if (!saleId && choice !== 'none') {
    const err = t('sales.postSaleReceiptMissingSale')
    store.setError(err)
    await ackReceiptChoiceSafely({ success: false, error: err })
    throw new Error(err)
  }

  store.markProcessing()

  const stillCurrentSale = () => {
    if (targetSaleId == null) {
      return true
    }
    const pending = store.pendingSaleId
    return pending == null || String(pending) === targetSaleId
  }

  try {
    if (choice === 'none') {
      if (stillCurrentSale()) {
        store.markReceiptComplete()
      }
      await ackReceiptChoiceSafely({ success: true })
      return { ok: true, choice: 'none' }
    }

    const sale = await posService.getSale(saleId)
    if (!stillCurrentSale()) {
      await ackReceiptChoiceSafely({ success: true })
      return { ok: false, choice, superseded: true }
    }

    if (store.pendingSaleSnapshot?.invoice_payment_context) {
      sale.invoice_payment_context = store.pendingSaleSnapshot.invoice_payment_context
    }

    if (choice === 'print') {
      const result = await runPrint(sale, { logoUrlFallback })
      if (stillCurrentSale()) {
        store.markReceiptComplete()
      }
      await ackReceiptChoiceSafely({ success: true })
      if (source === 'cashier' && result.needsBrowserPrint) {
        Notify.create({ type: 'info', message: t('sales.postSaleReceiptBrowserPrintHint') })
      }
      return { ok: true, choice: 'print', ...result }
    }

    if (choice === 'email') {
      let cc = ccMe
      if (source === 'cashier' && !ccMe) {
        try {
          const { useUserStore } = await import('@/stores/user')
          const userStore = useUserStore()
          if (!userStore.profile?.email) {
            await userStore.fetchProfile()
          }
          cc = !!userStore.profile?.cc_receipts_default
        } catch {
          cc = false
        }
      }
      await runEmail(sale, { email, updateCustomer: !!updateCustomer, ccMe: cc })
      Notify.create({ type: 'positive', message: t('sales.postSaleReceiptEmailed') })
      if (stillCurrentSale()) {
        store.markReceiptComplete()
      }
      await ackReceiptChoiceSafely({ success: true })
      return { ok: true, choice: 'email' }
    }

    if (choice === 'sms') {
      await runSms(sale, { phone, updateCustomer: !!updateCustomer })
      Notify.create({ type: 'positive', message: t('sales.postSaleReceiptTexted') })
      if (stillCurrentSale()) {
        store.markReceiptComplete()
      }
      await ackReceiptChoiceSafely({ success: true })
      return { ok: true, choice: 'sms' }
    }

    throw new Error(t('sales.postSaleReceiptUnknownChoice'))
  } catch (error) {
    const message = error?.response?.data?.message || error?.message || t('sales.postSaleReceiptFailed')
    if (stillCurrentSale()) {
      store.setError(message)
    }
    if (source === 'cashier' && stillCurrentSale()) {
      Notify.create({ type: 'negative', message })
    }
    await ackReceiptChoiceSafely({ success: false, error: message })
    throw error
  }
}

/** Handle CFD receipt preference events from boot listener. */
export async function handleCfdReceiptPreferenceEvent (payload) {
  const store = usePostSaleReceiptStore()
  if (store.status === 'processing') {
    return
  }
  const choice = String(payload?.choice || '')
  const saleId = payload?.saleId ?? store.pendingSaleId
  if (
    saleId != null
    && store.pendingSaleId != null
    && String(saleId) !== String(store.pendingSaleId)
  ) {
    return
  }
  const updateCustomer = payload?.updateCustomer !== false && !!store.customerId
  const value = String(payload?.value || '').trim()

  if (choice === 'email') {
    return executePostSaleReceiptChoice({
      saleId,
      choice: 'email',
      email: value,
      updateCustomer,
      source: 'cfd'
    })
  }
  if (choice === 'sms') {
    return executePostSaleReceiptChoice({
      saleId,
      choice: 'sms',
      phone: value,
      updateCustomer,
      source: 'cfd'
    })
  }
  if (choice === 'print') {
    const { useBusinessStore } = await import('@/stores/business')
    const businessStore = useBusinessStore()
    const logoUrlFallback =
      businessStore.info?.logo_url ||
      businessStore.info?.logo_original_url ||
      null
    return executePostSaleReceiptChoice({
      saleId,
      choice: 'print',
      source: 'cfd',
      logoUrlFallback
    })
  }
  if (choice === 'none') {
    return executePostSaleReceiptChoice({ saleId, choice: 'none', source: 'cfd' })
  }
}

export async function dismissPostSaleReceiptPending () {
  const store = usePostSaleReceiptStore()
  store.clearPending()
  await customerDisplayDismissPaymentComplete()
}

/**
 * Opens cashier receipt choices and notifies the customer display after payment.
 * @param {object} sale Finalized sale from getSale
 */
export async function beginPostSaleReceiptFlow (sale) {
  const store = usePostSaleReceiptStore()
  void customerDisplayDismissPaymentComplete()
  store.setPendingFromSale(sale)

  let offerOnCfd = false
  if (customerDisplayService.getCustomerDisplayEnabledFromStorage()) {
    if (customerDisplayService.isAndroidCustomerDisplayAvailable()) {
      try {
        const st = await customerDisplayService.getStatus()
        offerOnCfd = !!st.secondaryDisplayTouch
      } catch {
        offerOnCfd = false
      }
    } else if (customerDisplayService.isElectronCustomerDisplayAvailable()) {
      try {
        const st = await customerDisplayService.getStatus()
        offerOnCfd = !!(st.windowOpen && st.enabled)
      } catch {
        offerOnCfd = false
      }
    }
  }

  const hints = store.paymentCompletePayload()
  void customerDisplayService.notifyPaymentComplete({
    offerReceiptChoices: offerOnCfd,
    saleId: hints.saleId,
    customerId: hints.customerId,
    prefilledEmail: hints.prefilledEmail,
    prefilledPhone: hints.prefilledPhone
  })
}
